import { useState, useEffect } from 'react'
import { Button, Input } from '../common'
import { groupService } from '../../services'
import type { Group, GroupInsert, GroupUpdate } from '../../types/database.types'

export interface GroupFormData {
  name: string
  description: string
  category: string
}

interface GroupFormProps {
  group?: Group | null
  providerId: string
  onSubmit: (data: GroupInsert | GroupUpdate) => void
  onCancel: () => void
  isLoading?: boolean
}

export function GroupForm({
  group,
  providerId,
  onSubmit,
  onCancel,
  isLoading = false,
}: GroupFormProps) {
  const [formData, setFormData] = useState<GroupFormData>({
    name: '',
    description: '',
    category: 'custom',
  })
  const [errors, setErrors] = useState<Partial<GroupFormData>>({})

  const categories = groupService.getCategories()

  useEffect(() => {
    if (group) {
      setFormData({
        name: group.name,
        description: group.description || '',
        category: group.category || 'custom',
      })
    }
  }, [group])

  const validate = (): boolean => {
    const newErrors: Partial<GroupFormData> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Group name is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return

    const data = group
      ? {
          name: formData.name.trim(),
          description: formData.description.trim() || null,
          category: formData.category,
        }
      : {
          provider_id: providerId,
          name: formData.name.trim(),
          description: formData.description.trim() || null,
          category: formData.category,
        }

    onSubmit(data)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Group Name *
        </label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="e.g., Varsity Football, Monday PT Group"
          error={errors.name}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Category
        </label>
        <select
          value={formData.category}
          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          {categories.map((cat) => (
            <option key={cat.value} value={cat.value}>
              {cat.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          value={formData.description}
          onChange={(e) =>
            setFormData({ ...formData, description: e.target.value })
          }
          placeholder="Optional description for this group..."
          rows={3}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
        />
      </div>

      <div className="flex gap-3 pt-4">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading
            ? 'Saving...'
            : group
              ? 'Update Group'
              : 'Create Group'}
        </Button>
      </div>
    </form>
  )
}
